// Regras e engine de conflito offline-first
import { fieldPriority, classifyConflictSeverity } from './conflictRules';
function parseSyncTs(value) {
  if (!value) return 0;
  const n = Date.parse(value);
  return Number.isFinite(n) ? n : 0;
}

function itemSyncTs(item) {
  if (!item || typeof item !== "object") return 0;
  return Math.max(
    parseSyncTs(item.deletedAt),
    parseSyncTs(item.updatedAt),
    parseSyncTs(item.createdAt)
  );
}

function isDeletedItem(item) {
  return !!(item && typeof item === "object" && (item.deletedAt || item._deleted === true));
}

function stableJson(value) {
  const seen = new WeakSet();
  const normalize = (input) => {
    if (input === null || typeof input !== "object") return input;
    if (seen.has(input)) return null;
    seen.add(input);
    if (Array.isArray(input)) return input.map(normalize);
    const out = {};
    Object.keys(input).sort().forEach((key) => {
      if (["updatedAt","updatedBy","createdAt","deletedAt","deletedBy","_deleted","version","history"].includes(key)) return;
      out[key] = normalize(input[key]);
    });
    return out;
  };
  return JSON.stringify(normalize(value));
}


function chooseChangedSide({ collection, field, baseVal, serverVal, localVal, serverMetaTs, localMetaTs }) {
  const baseJson = stableJson(baseVal);
  const serverJson = stableJson(serverVal);
  const localJson = stableJson(localVal);
  const serverChanged = serverJson !== baseJson;
  const localChanged = localJson !== baseJson;
  const same = serverJson === localJson;

  if (same) return { value: localVal, source: 'same', reason: 'same_value' };
  if (localChanged && !serverChanged) return { value: localVal, source: 'local', reason: 'only_local_changed' };
  if (serverChanged && !localChanged) return { value: serverVal, source: 'server', reason: 'only_server_changed' };
  if (!localChanged && !serverChanged) return { value: baseVal, source: 'base', reason: 'unchanged' };

  const priority = fieldPriority(collection, field);
  if (Array.isArray(localVal) && Array.isArray(serverVal)) {
    const merged = [];
    const seen = new Set();
    for (const item of [...serverVal, ...localVal]) {
      const key = stableJson(item);
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(item);
      }
    }
    return { value: merged, source: 'merged', reason: 'merged_array_union', priority };
  }

  if (priority >= 95) return { conflict: true, source: 'manual', reason: 'critical_field_requires_admin', priority };
  if (priority >= 90 && localMetaTs === serverMetaTs) return { conflict: true, source: 'manual', reason: 'high_priority_tie_requires_admin', priority };
  if (localMetaTs > serverMetaTs) return { value: localVal, source: 'local', reason: 'newer_local', priority };
  if (serverMetaTs > localMetaTs) return { value: serverVal, source: 'server', reason: 'newer_server', priority };
  return { conflict: true, source: 'manual', reason: 'local_default_tie_break', priority };
}

function buildFieldConflict(collection, field, baseVal, serverVal, localVal, resolution, baseItem, serverItem, localItem) {
  return {
    field,
    priority: fieldPriority(collection, field),
    base: baseVal,
    server: serverVal,
    local: localVal,
    chosen: resolution.source,
    reason: resolution.reason,
    localUpdatedAt: localItem?.updatedAt || localItem?.deletedAt || null,
    serverUpdatedAt: serverItem?.updatedAt || serverItem?.deletedAt || null,
    baseUpdatedAt: baseItem?.updatedAt || baseItem?.deletedAt || null,
  };
}

function mergeDeletion(baseItem, serverItem, localItem, userName) {
  const baseDeleted = isDeletedItem(baseItem);
  const serverDeleted = isDeletedItem(serverItem);
  const localDeleted = isDeletedItem(localItem);
  const baseTs = itemSyncTs(baseItem);
  const serverTs = itemSyncTs(serverItem);
  const localTs = itemSyncTs(localItem);
  if (!baseDeleted && !serverDeleted && !localDeleted) return null;
  const winner = [
    { item: localItem, deleted: localDeleted, ts: localTs, source: 'local' },
    { item: serverItem, deleted: serverDeleted, ts: serverTs, source: 'server' },
    { item: baseItem, deleted: baseDeleted, ts: baseTs, source: 'base' },
  ].sort((a,b)=>b.ts-a.ts)[0];
  if (!winner?.deleted) return { deleted: false, source: winner?.source || 'none' };
  const item = winner.item || {};
  return {
    deleted: true,
    source: winner.source,
    payload: {
      _deleted: true,
      deletedAt: item.deletedAt || item.updatedAt || new Date().toISOString(),
      deletedBy: item.deletedBy || item.updatedBy || userName || 'system',
    }
  };
}

export function mergeItemThreeWay({ collection, id, baseItem, serverItem, localItem, userName }) {
  const allKeys = new Set([
    ...Object.keys(baseItem || {}),
    ...Object.keys(serverItem || {}),
    ...Object.keys(localItem || {}),
  ]);
  const merged = { id: id || baseItem?.id || serverItem?.id || localItem?.id };
  const fieldConflicts = [];
  const autoResolved = [];
  const localMetaTs = itemSyncTs(localItem);
  const serverMetaTs = itemSyncTs(serverItem);
  const baseMetaTs = itemSyncTs(baseItem);

  for (const key of allKeys) {
    if (key === 'id') continue;
    if (["updatedAt","updatedBy","createdAt","deletedAt","deletedBy","_deleted","version","history"].includes(key)) continue;
    const resolution = chooseChangedSide({
      collection,
      field: key,
      baseVal: baseItem?.[key],
      serverVal: serverItem?.[key],
      localVal: localItem?.[key],
      serverMetaTs,
      localMetaTs,
    });
    if (resolution?.conflict) {
      const payload = buildFieldConflict(collection, key, baseItem?.[key], serverItem?.[key], localItem?.[key], resolution, baseItem, serverItem, localItem);
      fieldConflicts.push(payload);
      merged[key] = baseItem && Object.prototype.hasOwnProperty.call(baseItem, key) ? baseItem?.[key] : serverItem?.[key];
      continue;
    }
    merged[key] = resolution.value;
    if (resolution.reason !== 'same_value' && resolution.reason !== 'unchanged') {
      const payload = buildFieldConflict(collection, key, baseItem?.[key], serverItem?.[key], localItem?.[key], resolution, baseItem, serverItem, localItem);
      autoResolved.push(payload);
    }
  }

  const deletion = mergeDeletion(baseItem, serverItem, localItem, userName);
  if (deletion?.deleted) Object.assign(merged, deletion.payload);

  const winnerTs = Math.max(baseMetaTs, serverMetaTs, localMetaTs, Date.now());
  const preferredSource = winnerTs === localMetaTs ? localItem : serverItem;
  merged.createdAt = baseItem?.createdAt || serverItem?.createdAt || localItem?.createdAt || new Date().toISOString();
  merged.updatedAt = new Date(winnerTs).toISOString();
  merged.updatedBy = preferredSource?.updatedBy || preferredSource?.deletedBy || userName || 'system';
  merged.version = Math.max(baseItem?.version || 0, serverItem?.version || 0, localItem?.version || 0) + ((autoResolved.length || fieldConflicts.length || deletion?.deleted) ? 1 : 0);
  const history = [];
  if (Array.isArray(baseItem?.history)) history.push(...baseItem.history);
  if (Array.isArray(serverItem?.history)) history.push(...serverItem.history);
  if (Array.isArray(localItem?.history)) history.push(...localItem.history);
  if (autoResolved.length || fieldConflicts.length || deletion?.deleted) {
    history.push({
      id: `hist_${Date.now()}_${Math.random().toString(36).slice(2,8)}`,
      at: new Date().toISOString(),
      by: userName || 'system',
      type: fieldConflicts.length ? 'conflict-merge' : 'auto-merge',
      fields: [...new Set([...autoResolved.map(f => f.field), ...fieldConflicts.map(f => f.field)])],
      deleted: !!deletion?.deleted,
    });
  }
  merged.history = history.slice(-30);

  return {
    merged,
    unresolved: fieldConflicts,
    autoResolved,
    severity: classifyConflictSeverity(collection, fieldConflicts.map(item => item.field)),
    confidence: Math.max(0, 100 - (fieldConflicts.length * 25) - (autoResolved.length * 5)),
  };
}
